
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { NursingHomeLeads } from "@/components/dashboard/NursingHomeLeads";
import { EnquiryTracker } from "@/components/dashboard/EnquiryTracker";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { ConsultationBanner } from "@/components/dashboard/ConsultationBanner";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { OnboardingWizard, OnboardingData } from "@/components/onboarding/OnboardingWizard";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { useSubscription } from "@/hooks/use-subscription";
import { useGuestSavesMigration } from "@/hooks/use-guest-saves-migration";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle, Mail, X, Search, ArrowRight, Crown, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const { subscribed: isPremium } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSubscribedBanner, setShowSubscribedBanner] = useState(() => searchParams.get("subscribed") === "true");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { nursingHomeLeads, waitlistData, isLoading, error, refetch } = useDashboardData(user, refreshTrigger);
  const isMobile = useIsMobile();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);
  const [showVerifyBanner, setShowVerifyBanner] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  // Check for pending signup (user just registered but email not confirmed yet)
  const pendingSignup = useMemo(() => {
    const raw = sessionStorage.getItem('pendingSignup');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }, []);
  
  // Auto-migrate guest saves to leads when user signs in
  useGuestSavesMigration();

  // Clean ?subscribed=true from URL after showing the banner
  useEffect(() => {
    if (searchParams.get("subscribed") === "true") {
      setShowSubscribedBanner(true);
      const next = new URLSearchParams(searchParams);
      next.delete("subscribed");
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to trigger data refresh
  const handleLeadUpdate = useCallback(() => {
    console.log("Updating leads...");
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Auto-add pending nursing home lead saved before signup redirect
  useEffect(() => {
    const addPendingLead = async () => {
      if (!user) return;
      const raw = localStorage.getItem('pendingLeadNH');
      if (!raw) return;
      localStorage.removeItem('pendingLeadNH');
      try {
        const { nhSlug, centerName, countySlug } = JSON.parse(raw);
        // Check it doesn't already exist
        const { data: existing } = await supabase
          .from('nursing_home_leads')
          .select('id')
          .eq('user_id', user.id)
          .eq('nursing_home_id', nhSlug)
          .limit(1);
        if (existing && existing.length > 0) return;
        await supabase.from('nursing_home_leads').insert({
          user_id: user.id,
          nursing_home_id: nhSlug,
          nursing_home_name: centerName,
          location: countySlug,
          status: 'waitlist',
          notes: 'Added via signup prompt',
        });
        toast.success(`${centerName} added to your list!`, {
          description: 'You can manage it from your dashboard.',
        });
        handleLeadUpdate();
      } catch (err) {
        console.error('Failed to add pending lead:', err);
      }
    };
    addPendingLead();
  }, [user, handleLeadUpdate]);

  // Check if user has completed onboarding
  useEffect(() => {
    const checkProfile = async () => {
      if (!user) return;
      
      const { data: profile } = await supabase
        .from("profiles")
        .select("saved_preferences")
        .eq("id", user.id)
        .maybeSingle();

      const hasPrefs = !!profile?.saved_preferences;
      setHasPreferences(hasPrefs);
      
      // Show onboarding wizard if user has no preferences AND just signed up
      const justCompleted = sessionStorage.getItem("justCompletedAuth");
      const justVerified = sessionStorage.getItem("justVerified");
      
      if (!hasPrefs && (justCompleted || justVerified)) {
        setShowOnboarding(true);
        sessionStorage.removeItem("justCompletedAuth");
        sessionStorage.removeItem("justVerified");
      } else {
        if (justVerified) {
          sessionStorage.removeItem("justVerified");
          handleLeadUpdate();
        }
        if (justCompleted) {
          sessionStorage.removeItem("justCompletedAuth");
        }
      }
      
      // Show verify banner if email not confirmed
      const emailConfirmed = user.email_confirmed_at || user.confirmed_at;
      setShowVerifyBanner(!emailConfirmed);
      
      setProfileChecked(true);
    };

    if (!authLoading && user) {
      checkProfile();
    }
  }, [user, authLoading, handleLeadUpdate]);

  const handleOnboardingComplete = async (data: OnboardingData) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          saved_preferences: {
            careNeeds: data.careNeeds,
            timeframe: data.timeframe,
            counties: data.counties,
            priorities: data.priorities,
            onboarding_completed_at: new Date().toISOString(),
          },
        })
        .eq("id", user.id);

      if (error) throw error;

      setHasPreferences(true);
      setShowOnboarding(false);
      toast.success("Preferences saved!", {
        description: "We'll show you personalised recommendations.",
      });
    } catch (err) {
      console.error("Error saving onboarding data:", err);
      toast.error("Couldn't save preferences. Please try again.");
    }
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    sessionStorage.removeItem("justCompletedAuth");
    sessionStorage.removeItem("justVerified");
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;
    setIsResendingVerification(true);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: user.email });
      if (error) throw error;
      toast.success("Verification email sent!", { description: "Check your inbox or spam folder." });
    } catch (err: any) {
      toast.error("Failed to send email", { description: err.message });
    } finally {
      setIsResendingVerification(false);
    }
  };


  // Redirect to login if not authenticated AND no pending signup
  if (!authLoading && !user && !pendingSignup) {
    return <Navigate to="/auth/login?redirectTo=/dashboard" />;
  }

  // Show pending signup dashboard (no session yet, waiting for email verification)
  if (!user && pendingSignup) {
    return (
      <Layout>
        <Helmet>
          <title>Welcome to Grandpal | Verify Your Email</title>
        </Helmet>
        <div className="container py-4 md:py-8 max-w-7xl px-3 md:px-4">
          <h1 className="text-2xl md:text-3xl font-semibold">
            Welcome{pendingSignup.firstName ? `, ${pendingSignup.firstName}` : ''}! 👋
          </h1>
          <p className="text-muted-foreground mt-1">Your account is almost ready.</p>

          {/* Verification banner */}
          <div className="mt-6 p-5 rounded-lg border border-amber-200 bg-amber-50">
            <div className="flex items-start gap-3">
              <Mail className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">Please verify your email to unlock your dashboard</p>
                <p className="text-sm text-amber-700 mt-1">
                  We've sent a verification link to <strong>{pendingSignup.email}</strong>. 
                  Click it to access your full dashboard with saved nursing homes, personalised recommendations, and more.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-800 hover:bg-amber-100"
                    onClick={async () => {
                      try {
                        const { error } = await supabase.auth.resend({ type: 'signup', email: pendingSignup.email });
                        if (error) throw error;
                        toast.success("Verification email resent!", { description: "Check your inbox or spam folder." });
                      } catch (err: any) {
                        toast.error("Failed to send email", { description: err.message });
                      }
                    }}
                  >
                    Resend verification email
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick-value content for unverified users */}
          <div className="mt-8 space-y-4">
            <h2 className="text-lg font-medium">While you wait, explore what Grandpal offers:</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Link to="/nursing-homes" className="group block p-5 rounded-lg border bg-card hover:border-primary/40 transition-colors">
                <div className="flex items-center gap-3">
                  <Search className="h-5 w-5 text-primary" />
                  <h3 className="font-medium group-hover:text-primary transition-colors">Browse Nursing Homes</h3>
                </div>
                <p className="text-sm text-muted-foreground mt-2">Search and compare over 500 nursing homes across Ireland.</p>
                <span className="text-sm text-primary mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
              <Link to="/resources/guides" className="group block p-5 rounded-lg border bg-card hover:border-primary/40 transition-colors">
                <div className="flex items-center gap-3">
                  <ArrowRight className="h-5 w-5 text-primary" />
                  <h3 className="font-medium group-hover:text-primary transition-colors">Read Our Guides</h3>
                </div>
                <p className="text-sm text-muted-foreground mt-2">Expert advice on finding the right care for your loved one.</p>
                <span className="text-sm text-primary mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Read guides <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show onboarding wizard
  if (showOnboarding && user) {
    return (
      <Layout>
        <Helmet>
          <title>Welcome to Grandpal | Set Up Your Preferences</title>
        </Helmet>
        <div className="container py-8 md:py-16 max-w-7xl px-3 md:px-4">
          <OnboardingWizard
            firstName={user.user_metadata?.first_name}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>My Dashboard | Grandpal</title>
      </Helmet>

      <div className="container py-4 md:py-8 max-w-7xl px-3 md:px-4">
        <DashboardHeader user={user} isPremium={isPremium} />

        {/* Premium welcome banner — shown once after successful Stripe checkout */}
        {showSubscribedBanner && (
          <div className="relative mt-4 rounded-xl border border-primary/30 bg-primary/5 p-5 flex items-start gap-4">
            <div className="flex-shrink-0 flex items-center justify-center h-11 w-11 rounded-full bg-primary/10">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                Welcome to Grandpal Premium! 🎉
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Your subscription is active. Managed enquiries, the full tracker, weekly updates, and priority expert access are now unlocked.
              </p>
            </div>
            <button
              onClick={() => setShowSubscribedBanner(false)}
              className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Email verification banner */}
        {showVerifyBanner && (
          <div className="relative flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 mt-4">
            <Mail className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">Please verify your email</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Check your inbox for a verification link to keep your account secure.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={handleResendVerification}
                disabled={isResendingVerification}
              >
                {isResendingVerification ? "Sending..." : "Resend verification email"}
              </Button>
            </div>
            <button onClick={() => setShowVerifyBanner(false)} className="text-amber-400 hover:text-amber-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {isLoading || authLoading || !profileChecked ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center">
              <Loader2 className="h-10 w-10 text-grandpal-green animate-spin mb-4" />
              <p className="text-gray-600">Loading your dashboard...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 mb-4">We encountered an issue while loading your dashboard data.</p>
            <button 
              onClick={refetch}
              className="px-4 py-2 bg-grandpal-green text-white rounded-md hover:bg-grandpal-green-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Onboarding checklist for new users */}
            <OnboardingChecklist
              hasPreferences={hasPreferences}
              hasLeads={nursingHomeLeads.length > 0}
              hasCareRecipient={false}
              hasBookedConsultation={false}
            />
            
            <ConsultationBanner />

            {/* Go Premium nudge banner — hidden for subscribers */}
            {!isPremium && (
              <Link
                to="/premium"
                className="flex items-center gap-4 p-4 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 hover:border-amber-300 transition-colors group"
              >
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-amber-100">
                  <Crown className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-amber-900 text-sm">Upgrade to Premium — €12.99/month</p>
                  <p className="text-amber-700 text-xs mt-0.5">Managed enquiries, full tracker, weekly updates & more.</p>
                </div>
                <ArrowRight className="h-4 w-4 text-amber-500 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}

            {/* Enquiry Tracker */}
            <div>
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-grandpal-green-dark">My Enquiry Tracker</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Track every home you've contacted, update statuses, add notes, and set follow-up reminders.
                </p>
              </div>
              <EnquiryTracker
                leads={nursingHomeLeads}
                onLeadUpdate={handleLeadUpdate}
                isPremium={isPremium}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
